///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Destructive storage operations used by the Storage Manager dev tool.
/// All functions throw on failure; callers handle confirmation and reporting.

import { logger } from '@/utils/LogDispatcher'
import type { IndexedDBInfo, StorageSnapshot } from '@/components/features/dev-tools/storage-manager/storageScanner'

/// Thrown when OPFS deletion fails because the SQLite worker VFS holds
/// FileSystemSyncAccessHandle locks; a page reload releases them
export class VFSLockedError extends Error {
	constructor(target: string) {
		super(`${target} is locked by an active VFS. Reload the page to release locks, then try again.`)
		this.name = 'VFSLockedError'
	}
}

function isLockError(error: unknown): boolean {
	const err = error instanceof Error ? error : null
	return !!err && (err.name === 'NoModificationAllowedError' || err.message.includes('in use'))
}

export async function deleteVFSFile(volumeName: string, fileName: string): Promise<void> {
	const opfsRoot = await navigator.storage.getDirectory()
	const vfsDir = await opfsRoot.getDirectoryHandle(volumeName, { create: false })
	try {
		await vfsDir.removeEntry(fileName)
		logger.info(`Deleted VFS file: ${fileName}`)
	} catch (error) {
		if (isLockError(error)) throw new VFSLockedError(fileName)
		throw error
	}
}

export async function deleteVFSVolume(volumeName: string): Promise<void> {
	const opfsRoot = await navigator.storage.getDirectory()
	try {
		await opfsRoot.removeEntry(volumeName, { recursive: true })
		logger.info(`Deleted VFS volume: ${volumeName}`)
	} catch (error) {
		if (isLockError(error)) throw new VFSLockedError(volumeName)
		throw error
	}
}

export async function deleteGeoPackageFile(fileName: string): Promise<void> {
	const opfsRoot = await navigator.storage.getDirectory()
	const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: false })
	await gpkgDir.removeEntry(fileName)
	logger.info(`Deleted GeoPackage file: ${fileName}`)
}

export async function deleteIndexedDBDatabase(dbName: string): Promise<void> {
	await new Promise<void>((resolve, reject) => {
		const request = indexedDB.deleteDatabase(dbName)
		request.onsuccess = () => resolve()
		request.onerror = () => reject(request.error ?? new Error('Failed to delete IndexedDB database'))
		request.onblocked = () => reject(new Error('Deletion blocked - close all connections and try again'))
	})
	logger.info(`Deleted IndexedDB database: ${dbName}`)
}

export async function clearObjectStore(dbName: string, storeName: string): Promise<void> {
	const db = await new Promise<IDBDatabase>((resolve, reject) => {
		const request = indexedDB.open(dbName)
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB database'))
	})

	try {
		const transaction = db.transaction(storeName, 'readwrite')
		const store = transaction.objectStore(storeName)
		await new Promise<void>((resolve, reject) => {
			const request = store.clear()
			request.onsuccess = () => resolve()
			request.onerror = () => reject(request.error ?? new Error('Failed to clear object store'))
		})
		logger.info(`Cleared object store "${storeName}" from database: ${dbName}`)
	} finally {
		db.close()
	}
}

/// Rebuild a database from its own contents to reclaim orphaned storage space.
/// Callers should reload the page afterwards to ensure clean connections.
export async function vacuumIndexedDBDatabase(dbInfo: IndexedDBInfo): Promise<void> {
	const dbName = dbInfo.name

	const db = await new Promise<IDBDatabase>((resolve, reject) => {
		const request = indexedDB.open(dbName)
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB database'))
	})

	// Export all data from all object stores
	const exportedData: Record<string, unknown[]> = {}
	for (const storeInfo of dbInfo.objectStores) {
		const transaction = db.transaction(storeInfo.name, 'readonly')
		const store = transaction.objectStore(storeInfo.name)
		exportedData[storeInfo.name] = await new Promise<unknown[]>((resolve) => {
			const request = store.getAll()
			request.onsuccess = () => resolve(request.result || [])
			request.onerror = () => resolve([])
		})
	}

	const version = db.version
	const objectStoreConfigs = dbInfo.objectStores.map(s => ({
		name: s.name,
		keyPath: s.keyPath,
		autoIncrement: s.autoIncrement,
	}))
	db.close()

	// Delete and recreate the database
	await new Promise<void>((resolve, reject) => {
		const request = indexedDB.deleteDatabase(dbName)
		request.onsuccess = () => resolve()
		request.onerror = () => reject(request.error ?? new Error('Failed to delete IndexedDB database'))
		request.onblocked = () => reject(new Error('Database deletion blocked'))
	})

	const newDb = await new Promise<IDBDatabase>((resolve, reject) => {
		const request = indexedDB.open(dbName, version)
		request.onupgradeneeded = () => {
			const db = request.result
			for (const config of objectStoreConfigs) {
				if (!db.objectStoreNames.contains(config.name)) {
					db.createObjectStore(config.name, {
						keyPath: config.keyPath || undefined,
						autoIncrement: config.autoIncrement,
					})
				}
			}
		}
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB database'))
	})

	// Restore all data
	for (const [storeName, records] of Object.entries(exportedData)) {
		if (records.length > 0) {
			const transaction = newDb.transaction(storeName, 'readwrite')
			const store = transaction.objectStore(storeName)
			for (const record of records) {
				store.add(record)
			}
		}
	}
	newDb.close()

	logger.info(`Vacuumed IndexedDB database: ${dbName}`)
}

export function exportSnapshot(snapshot: StorageSnapshot): void {
	const data = {
		timestamp: new Date().toISOString(),
		usage: snapshot.usage,
		quota: snapshot.quota,
		isPersisted: snapshot.isPersisted,
		opfsTotal: snapshot.opfsTotal,
		volumes: snapshot.volumes,
		geopackages: snapshot.geopackages,
		indexedDBs: snapshot.indexedDBs.map(db => ({
			name: db.name,
			version: db.version,
			objectStores: db.objectStores.map(({ sampleKeys: _, ...rest }) => rest),
		})),
		localStorageKeys: snapshot.localStorageItems.map(item => ({ key: item.key, size: item.size })),
	}
	const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = `storage-manager-${Date.now()}.json`
	a.click()
	URL.revokeObjectURL(url)
}
